/**
 * Planner page — SVAR React Gantt rendering layer.  Backend contract
 * (endpoints in `stage5_planner.py`) is frozen and unmodified.
 *
 * Design notes
 * ------------
 * SVAR MIT does NOT ship task-segments (that's a PRO feature), so we
 * model each project as a `summary` parent row and each planner item
 * as a child task under it.  The summary bar in the timeline is
 * hidden via CSS so a project row shows only the left-column meta;
 * every item then renders on its own child row directly below.
 */
import React, {
    useCallback, useEffect, useMemo, useRef, useState,
} from "react";
import { Gantt, Willow } from "@svar-ui/react-gantt";
import "@svar-ui/react-gantt/all.css";
import { Plus, Settings } from "lucide-react";
import { toast } from "sonner";
import {
    addDays, differenceInCalendarDays, endOfISOWeek, endOfYear,
    format as fmt, getISOWeek, isValid, parseISO, startOfISOWeek,
} from "date-fns";
import { api } from "../../lib/api";
import { usePageMeta } from "../../components/Layout";

/* ---------- Category catalogue --------------------------------------- */
const CATEGORY_OPTIONS = [
    { value: "ws2", label: "WS2 — Project Feasibility" },
    { value: "ws3", label: "WS3 — Concept Design" },
    { value: "ws4", label: "WS4 — Developed + Technical Design" },
    { value: "ws5", label: "WS5 — Pre-Construction" },
    { value: "ws6", label: "WS6 — Construction" },
    { value: "ws7", label: "WS7 — Post-Construction" },
    { value: "other", label: "Other" },
];
const DEFAULT_COLOURS = {
    ws2: "#7B92A8", ws3: "#C9954B", ws4: "#B57A85", ws5: "#86A57A",
    ws6: "#A8543F", ws7: "#1D1D1B", other: "#8A8F94",
};

/* ---------- Pill (mirrors ProjectsLanding "active" tone) ------------ */
function Pill({ children, testid }) {
    return (
        <span data-testid={testid}
              className="inline-flex items-center px-2 py-0.5 rounded-full text-[8px] font-medium bg-[#E8F5E9] text-[#2E7D32]">
            {children}
        </span>
    );
}

/* ---------- Two-line header cell formatters -------------------------- */
function weekCellLabel(d) {
    const s = startOfISOWeek(d);
    const e = endOfISOWeek(d);
    // \n is the two-line separator; CSS `white-space: pre-line`
    // (see the injected <style> block below) turns it into a break.
    return `W${getISOWeek(s)}\n${fmt(s, "dd/MM")}\u2013${fmt(e, "dd/MM")}`;
}

/* ---------- Category helpers ---------------------------------------- */
function categoryDisplay(item) {
    if (!item) return "";
    if (item.category === "other") {
        return item.category_other_text
            ? `Other — ${item.category_other_text}`
            : "Other";
    }
    return CATEGORY_OPTIONS.find(o => o.value === item.category)?.label
        || item.category;
}

/* ==================================================================== *
 * Add / Edit dialog (retained from Phase A, minor prop tweaks)
 * ==================================================================== */
function PlannerItemDialog({
    mode, initialItem, projects, lockProject, onClose, onSaved, onDeleted,
}) {
    // `lockProject` (Comment #4) — when true the project field is
    // rendered as a read-only display (no dropdown, no change).
    const [form, setForm] = useState(() => ({
        project_id: initialItem?.project_id || (projects[0]?.id ?? ""),
        name: initialItem?.name || "",
        category: initialItem?.category || "ws2",
        category_other_text: initialItem?.category_other_text || "",
        start_date: initialItem?.start_date || fmt(new Date(), "yyyy-MM-dd"),
        duration_days: initialItem?.duration_days ?? 1,
    }));
    const [saving, setSaving] = useState(false);
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const testid = mode === "edit"
        ? "planner-edit-dialog" : "planner-create-dialog";
    const projectName = useMemo(() => {
        const p = projects.find(x => x.id === form.project_id);
        return p ? (p.client_name || "—") : "—";
    }, [projects, form.project_id]);

    const save = async () => {
        const name = form.name.trim();
        if (!name) { toast.error("Name is required"); return; }
        const dur = Number(form.duration_days);
        if (!Number.isInteger(dur) || dur < 1) {
            toast.error("Duration must be a whole number \u2265 1"); return;
        }
        if (form.category === "other" && !form.category_other_text.trim()) {
            toast.error("Custom label is required for Other"); return;
        }
        const payload = {
            name, category: form.category,
            category_other_text: form.category === "other"
                ? form.category_other_text.trim() : null,
            start_date: form.start_date,
            duration_days: dur,
        };
        setSaving(true);
        try {
            let resp;
            if (mode === "edit") {
                resp = await api.patch(
                    `/admin/planner/items/${initialItem.id}`, payload);
            } else {
                if (!form.project_id) {
                    toast.error("Project is required");
                    setSaving(false); return;
                }
                resp = await api.post("/admin/planner/items", {
                    project_id: form.project_id, ...payload,
                });
            }
            toast.success(mode === "edit"
                ? "Planner item saved" : "Planner item added");
            onClose();
            onSaved(resp.data?.item);
        } catch (e) {
            toast.error(e?.response?.data?.detail
                || "Could not save planner item");
        } finally { setSaving(false); }
    };

    const doDelete = async () => {
        setDeleting(true);
        try {
            await api.delete(`/admin/planner/items/${initialItem.id}`);
            toast.success("Planner item deleted");
            onClose();
            onDeleted(initialItem.id);
        } catch (e) {
            toast.error(e?.response?.data?.detail
                || "Could not delete planner item");
        } finally { setDeleting(false); }
    };

    return (
        <div className="fixed inset-y-0 left-0 right-0 md:left-[268px] bg-black/30 z-40 flex items-center justify-center"
             role="dialog" aria-modal="true" data-testid={testid}>
            <div className="bg-white rounded-lg p-4 w-[460px] flex flex-col gap-3">
                <h3 className="font-manrope font-semibold text-charcoal">
                    {mode === "edit" ? "Edit planner item" : "Add planner item"}
                </h3>
                {/* Project field — locked when opened from the row + button. */}
                {mode === "create" && !lockProject ? (
                    <label className="flex flex-col gap-1 text-[12px] text-[#666]">
                        Project
                        <select value={form.project_id}
                                onChange={(e) => setForm(f => ({ ...f, project_id: e.target.value }))}
                                className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px] text-charcoal bg-white"
                                data-testid={`${testid}-project`}>
                            {projects.map(p => (
                                <option key={p.id} value={p.id}>
                                    {p.client_name || "\u2014"}
                                    {p.address_subtitle ? ` \u00b7 ${p.address_subtitle}` : ""}
                                </option>
                            ))}
                        </select>
                    </label>
                ) : (
                    <div className="text-[12px] text-[#666]"
                         data-testid={`${testid}-project-readonly`}>
                        Project: <span className="text-charcoal font-medium">{projectName}</span>
                    </div>
                )}
                <label className="flex flex-col gap-1 text-[12px] text-[#666]">
                    Name
                    <input value={form.name}
                           onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                           maxLength={200}
                           className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px] text-charcoal"
                           data-testid={`${testid}-name`} />
                </label>
                <label className="flex flex-col gap-1 text-[12px] text-[#666]">
                    Category
                    <select value={form.category}
                            onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))}
                            className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px] text-charcoal bg-white"
                            data-testid={`${testid}-category`}>
                        {CATEGORY_OPTIONS.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                </label>
                {form.category === "other" && (
                    <label className="flex flex-col gap-1 text-[12px] text-[#666]">
                        Custom label
                        <input value={form.category_other_text}
                               onChange={(e) => setForm(f => ({ ...f, category_other_text: e.target.value }))}
                               maxLength={100}
                               className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px] text-charcoal"
                               data-testid={`${testid}-other-text`} />
                    </label>
                )}
                <div className="grid grid-cols-2 gap-3">
                    <label className="flex flex-col gap-1 text-[12px] text-[#666]">
                        Start date
                        <input type="date" value={form.start_date}
                               onChange={(e) => setForm(f => ({ ...f, start_date: e.target.value }))}
                               className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px] text-charcoal"
                               data-testid={`${testid}-start`} />
                    </label>
                    <label className="flex flex-col gap-1 text-[12px] text-[#666]">
                        Duration (days)
                        <input type="number" min={1} value={form.duration_days}
                               onChange={(e) => setForm(f => ({ ...f, duration_days: e.target.value }))}
                               className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[13px] text-charcoal"
                               data-testid={`${testid}-duration`} />
                    </label>
                </div>
                {confirmingDelete ? (
                    <div className="border border-terracotta/30 rounded-md p-3 bg-[#FDF4F2] flex flex-col gap-2"
                         data-testid={`${testid}-confirm-delete`}>
                        <div className="text-[13px] text-charcoal">
                            Delete <span className="font-semibold">"{initialItem?.name}"</span>?
                            This cannot be undone.
                        </div>
                        <div className="flex gap-2 justify-end">
                            <button onClick={() => setConfirmingDelete(false)}
                                    className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md text-charcoal"
                                    data-testid={`${testid}-confirm-delete-cancel`}>Cancel</button>
                            <button onClick={doDelete} disabled={deleting}
                                    className="text-[13px] px-3 py-1.5 bg-terracotta text-white rounded-md disabled:opacity-50"
                                    data-testid={`${testid}-confirm-delete-yes`}>
                                {deleting ? "Deleting\u2026" : "Delete"}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="flex gap-2 justify-end mt-1">
                        {mode === "edit" && (
                            <button onClick={() => setConfirmingDelete(true)}
                                    className="mr-auto text-[13px] px-3 py-1.5 border border-terracotta/40 text-terracotta rounded-md"
                                    data-testid={`${testid}-delete`}>Delete</button>
                        )}
                        <button onClick={onClose}
                                className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md text-charcoal"
                                data-testid={`${testid}-cancel`}>Cancel</button>
                        <button onClick={save} disabled={saving}
                                className="text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-md disabled:opacity-50"
                                data-testid={`${testid}-submit`}>
                            {saving ? "Saving\u2026" : "Save"}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

/* ==================================================================== *
 * Settings cogwheel (Comment #6 not in this section but per §6/§B):
 * per-category colour panel, debounced autosave.
 * ==================================================================== */
function ColourSettingsPanel({ colours, onChange, onReset, onClose }) {
    return (
        <div className="fixed inset-y-0 left-0 right-0 md:left-[268px] bg-black/30 z-40 flex items-center justify-center"
             role="dialog" aria-modal="true" data-testid="planner-settings-dialog">
            <div className="bg-white rounded-lg shadow-xl w-[480px] flex flex-col">
                <div className="flex items-center px-5 py-4 border-b border-[#E5E2DC]">
                    <h3 className="font-manrope font-semibold text-charcoal text-[15px]">
                        Category colours
                    </h3>
                    <button onClick={onClose}
                            className="ml-auto text-[13px] px-3 py-1 border border-[#E5E2DC] rounded-md text-charcoal hover:bg-[#FAFAF7]"
                            data-testid="planner-settings-close">Close</button>
                </div>
                <div className="flex flex-col divide-y divide-[#E5E2DC]">
                    {CATEGORY_OPTIONS.map(opt => {
                        const hex = colours[opt.value] || DEFAULT_COLOURS[opt.value];
                        return (
                            <div key={opt.value}
                                 className="flex items-center gap-3 px-5 py-3"
                                 data-testid={`planner-settings-row-${opt.value}`}>
                                <span className="text-[13px] text-charcoal font-medium flex-1 truncate">
                                    {opt.label}
                                </span>
                                <span aria-hidden="true"
                                      className="inline-block w-5 h-5 rounded border border-[#E5E2DC] shrink-0"
                                      style={{ background: hex }} />
                                <input type="text" value={hex}
                                       onChange={(e) => onChange(opt.value, e.target.value)}
                                       maxLength={7}
                                       className="border border-[#E5E2DC] rounded-md px-2 py-1 text-[12px] text-charcoal w-[96px] font-mono uppercase"
                                       data-testid={`planner-settings-hex-${opt.value}`} />
                                <input type="color" value={hex}
                                       onChange={(e) => onChange(opt.value, e.target.value)}
                                       className="w-8 h-8 border border-[#E5E2DC] rounded cursor-pointer p-0"
                                       data-testid={`planner-settings-picker-${opt.value}`} />
                            </div>
                        );
                    })}
                </div>
                <div className="flex justify-end px-5 py-3 border-t border-[#E5E2DC] bg-[#FAFAF7]">
                    <button onClick={onReset}
                            className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md text-charcoal hover:bg-white"
                            data-testid="planner-settings-reset">
                        Reset to defaults
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ==================================================================== *
 * Main Planner page
 * ==================================================================== */
export default function Planner() {
    usePageMeta({ title: "Planner | Studio G4 Admin" });
    const [year, setYear] = useState(() => new Date().getFullYear());
    const [projects, setProjects] = useState([]);
    const [preferences, setPreferences] = useState(null);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState(null);
    const [showSettings, setShowSettings] = useState(false);
    const wrapperRef = useRef(null);
    // SVAR api handle (grabbed via `init` prop) — Comment #6.
    const svarApiRef = useRef(null);
    // Debounce timer for preferences autosave.
    const prefsSaveTimer = useRef(null);
    // Suppression flag: when true, the next `add-task` / `update-task`
    // event was generated locally (e.g. reload after PATCH) and should
    // NOT re-round-trip to the backend.
    const suppressLocalEvents = useRef(false);

    const colours = preferences?.category_colours || DEFAULT_COLOURS;

    /* -------- Load projects + items + prefs -------------------------- */
    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [pr, it] = await Promise.all([
                api.get("/admin/planner/projects"),
                api.get(`/admin/planner/items?year=${year}`),
            ]);
            setProjects(pr.data?.items || []);
            setPreferences(pr.data?.preferences || null);
            setItems(it.data?.items || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail
                || "Could not load planner");
        } finally { setLoading(false); }
    }, [year]);
    useEffect(() => { loadAll(); }, [loadAll]);

    /* -------- Build SVAR tasks: parent-per-project, child-per-item --- */
    const svarData = useMemo(() => {
        // Placeholder date range for a project parent — SVAR requires
        // a start/end even for a purely-organisational parent.  The
        // parent bar is hidden via CSS so the dates are never visible.
        // `type: "task"` (not "summary") avoids SVAR's auto-aggregate
        // path that crashes when children move.  `open: true` only
        // when the project has at least one item — otherwise SVAR's
        // tree walker (which does `open===true && recurse(node.data)`
        // without a null-check) crashes on an empty parent.
        const anchor = new Date(year, 0, 1);
        const anchorEnd = new Date(year, 0, 2);
        const projectHasItems = new Set();
        for (const it of items) projectHasItems.add(it.project_id);
        const tasks = [];
        for (const p of projects) {
            tasks.push({
                id: `p:${p.id}`,
                text: p.client_name || "\u2014",
                type: "task",
                open: projectHasItems.has(p.id),
                start: anchor,
                end: anchorEnd,
                duration: 1,
                _isProject: true,
                _project: p,
            });
        }
        for (const it of items) {
            const start = parseISO(it.start_date);
            if (!isValid(start)) continue;
            const end = addDays(start, it.duration_days);   // exclusive
            tasks.push({
                id: it.id,
                text: it.name,
                type: it.duration_days === 1 ? "milestone" : "task",
                start, end, duration: it.duration_days,
                parent: `p:${it.project_id}`,
                _isItem: true, _item: it,
            });
        }
        return { tasks };
    }, [projects, items, year]);

    /* -------- Scales & columns for SVAR ------------------------------ */
    const scales = useMemo(() => ([
        { unit: "week",  step: 1, format: weekCellLabel,
          css: () => "planner-week-cell" },
    ]), []);

    const columns = useMemo(() => ([
        {
            id: "text",
            header: "Projects",
            width: 320,
            cell: ({ row }) => {
                if (row._isProject) {
                    const p = row._project;
                    return (
                        <div className="flex items-start gap-2 w-full h-full pt-2 pb-2"
                             data-testid={`planner-row-${p.id}`}>
                            {/* + button — positioned on the LEFT of the
                                cell content (SVAR renders its collapse
                                triangle before this cell, so the + sits
                                immediately to the right of the triangle). */}
                            <button onMouseDown={(e) => { e.stopPropagation(); }}
                                    onPointerDown={(e) => { e.stopPropagation(); }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openCreateForProject(p.id);
                                    }}
                                    title="Add planner item"
                                    aria-label="Add planner item"
                                    className="shrink-0 w-4 h-4 rounded text-[#666] hover:bg-[#EBE8E0] hover:text-charcoal flex items-center justify-center transition-colors mt-[1px]"
                                    data-testid={`planner-add-${p.id}`}>
                                <Plus size={10} />
                            </button>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 min-w-0 leading-tight">
                                    <span className="text-[12px] font-semibold text-terracotta truncate"
                                          data-testid={`planner-row-client-${p.id}`}>
                                        {p.client_name || "\u2014"}
                                    </span>
                                    <Pill testid={`planner-row-ws-${p.id}`}>
                                        WS {p.current_workstage}
                                    </Pill>
                                </div>
                                <div className="text-[10px] text-[#666] truncate leading-tight"
                                     style={{ marginTop: 2 }}
                                     title={p.address_subtitle || ""}
                                     data-testid={`planner-row-addr-${p.id}`}>
                                    {p.address_subtitle || "\u2014"}
                                </div>
                            </div>
                        </div>
                    );
                }
                // Child item row — just the item name (1pt smaller
                // than the project name).  Indented 24px so children
                // read as nested under their parent project row.
                return (
                    <div className="text-[11px] text-charcoal truncate w-full pl-6"
                         data-testid={`planner-item-name-${row.id}`}>
                        {row.text}
                    </div>
                );
            },
        },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ]), [projects]);

    /* -------- Local optimistic mutators ------------------------------ */
    const applyItemPatch = useCallback((id, patch) => {
        setItems(prev => prev.map(x => x.id === id ? { ...x, ...patch } : x));
    }, []);
    const revertItem = useCallback((prevRow) => {
        setItems(prev => prev.map(x => x.id === prevRow.id ? prevRow : x));
    }, []);

    /* -------- Preferences autosave (debounced) ----------------------- */
    const savePreferences = useCallback(async (body) => {
        try {
            const resp = await api.patch(
                "/admin/me/planner-preferences", body);
            setPreferences(resp.data?.preferences || null);
        } catch (e) {
            toast.error(e?.response?.data?.detail
                || "Could not save planner preferences");
        }
    }, []);
    const queuePrefsSave = useCallback((body) => {
        if (prefsSaveTimer.current) clearTimeout(prefsSaveTimer.current);
        prefsSaveTimer.current = setTimeout(
            () => savePreferences(body), 500);
    }, [savePreferences]);

    /* -------- Dialog openers ----------------------------------------- */
    const openCreateForProject = useCallback((projectId) => {
        setDialog({
            mode: "create",
            initialItem: { project_id: projectId },
            lockProject: true,  // Comment #4
        });
    }, []);
    const openEdit = useCallback((item) => {
        setDialog({ mode: "edit", initialItem: item, lockProject: true });
    }, []);
    const onSaved = useCallback((saved) => {
        if (!saved) return;
        setItems(prev => {
            const idx = prev.findIndex(x => x.id === saved.id);
            if (idx === -1) return [...prev, saved];
            const clone = [...prev]; clone[idx] = saved;
            return clone;
        });
    }, []);
    const onDeleted = useCallback(
        (id) => setItems(prev => prev.filter(x => x.id !== id)), []);

    /* -------- SVAR event wiring via `init` prop ---------------------- */
    const initGantt = useCallback((apiObj) => {
        svarApiRef.current = apiObj;

        // Item bar drag OR resize → PATCH backend.
        apiObj.on("update-task", (ev) => {
            if (suppressLocalEvents.current) return;
            const task = ev.task;
            if (!task || !task._isItem) return;
            // Compute the new start_date + duration_days from SVAR
            // Date objects.  Snap to whole days for both.
            const start = task.start instanceof Date
                ? task.start : parseISO(task.start);
            const end = task.end instanceof Date
                ? task.end : parseISO(task.end);
            const dur = Math.max(
                1, differenceInCalendarDays(end, start) || 1);
            const newStartIso = fmt(start, "yyyy-MM-dd");
            const item = task._item;
            if (newStartIso === item.start_date
                && dur === item.duration_days) return;
            // Optimistic local patch; revert on failure.
            const previous = { ...item };
            applyItemPatch(item.id, {
                start_date: newStartIso, duration_days: dur,
            });
            api.patch(`/admin/planner/items/${item.id}`, {
                start_date: newStartIso, duration_days: dur,
            }).then((resp) => {
                if (resp.data?.item) onSaved(resp.data.item);
            }).catch((e) => {
                revertItem(previous);
                toast.error(e?.response?.data?.detail
                    || "Could not update planner item");
            });
        });

        // Task click (not drag) → open edit dialog.  SVAR fires
        // `select-task` on click; a drag ends via `update-task` and
        // does NOT fire this event, giving us click-vs-drag OOTB.
        apiObj.on("select-task", (ev) => {
            const t = apiObj.getTask(ev.id);
            if (!t) return;
            if (t._isItem) openEdit(t._item);
        });

        // Row reorder → project_order patch.  SVAR fires `move-task`
        // (mode="before"/"after") on drop.  Re-read the ordering
        // from the tree store after the move commits.
        apiObj.on("move-task", (ev) => {
            const moved = apiObj.getTask(ev.id);
            if (!moved || !moved._isProject) return;
            const state = apiObj.getState();
            const allTasks = typeof state.tasks?.toArray === "function"
                ? state.tasks.toArray()
                : (Array.isArray(state.tasks) ? state.tasks : []);
            const rootIds = allTasks
                .filter(t => t._isProject && t._project)
                .map(t => t._project.id);
            queuePrefsSave({ project_order: rootIds });
        });
    }, [applyItemPatch, revertItem, onSaved, openEdit, queuePrefsSave]);

    /* -------- Range + autoscroll (Requests: full-year range, ---------
       current month left-aligned) ----------------------------------- */
    const rangeStart = useMemo(() => new Date(year, 0, 1), [year]);
    // 31 Dec 23:59:59.999 of the viewed year — ensures the last ISO
    // week of the year is included in SVAR's week scale.
    const rangeEnd = useMemo(() => endOfYear(new Date(year, 0, 1)), [year]);
    const CELL_WIDTH_PX = 100;   // matches <Gantt cellWidth={100}/>

    const scrollToCurrentMonth = useCallback(() => {
        // Left-align the current-month column at the start of the
        // visible viewport (not centre, not right).  SVAR renders a
        // small buffer of cells BEFORE the `start` prop, so the DOM
        // origin (scrollLeft=0) is a couple of weeks earlier than
        // Jan 1.  Read the true internal `_start` from SVAR's state
        // and compute the pixel offset from there.
        const today = new Date();
        const yearInView = year;
        const anchor = new Date(yearInView,
            today.getFullYear() === yearInView ? today.getMonth() : 0, 1);
        const target = svarApiRef.current;
        let targetLeft = 0;
        if (target) {
            try {
                const state = target.getState();
                const svarStart = state?._start;
                const cw = state?.cellWidth || CELL_WIDTH_PX;
                if (svarStart) {
                    // SVAR quantises to week cells; anchor to the
                    // Monday of the ISO week containing the target so
                    // its column starts flush with the viewport left.
                    const anchorWeekStart = startOfISOWeek(anchor);
                    const daysDiff = differenceInCalendarDays(
                        anchorWeekStart, svarStart);
                    targetLeft = Math.max(0,
                        Math.round(daysDiff * (cw / 7)));
                }
            } catch (_) { /* fall through */ }
            try {
                target.exec("scroll-chart", { left: targetLeft });
                return;
            } catch (_) { /* fall through */ }
        }
        // DOM fallback (approximate — assumes cellWidth == 100 and
        // no leading buffer; only used if SVAR API is unavailable).
        const chart = wrapperRef.current?.querySelector(".wx-chart");
        if (chart) {
            const daysFromJan = differenceInCalendarDays(anchor, rangeStart);
            chart.scrollLeft = Math.max(0,
                Math.round(daysFromJan * (CELL_WIDTH_PX / 7)));
        }
    }, [year, rangeStart, CELL_WIDTH_PX]);

    // Run once after the initial load finishes and the tasks paint.
    useEffect(() => {
        if (loading || !projects.length) return;
        const t = setTimeout(scrollToCurrentMonth, 300);
        return () => clearTimeout(t);
    }, [loading, projects.length, scrollToCurrentMonth]);

    /* -------- Drag-to-scroll on the time-header (Comment #3) --------- */
    useEffect(() => {
        if (loading) return;
        const wrap = wrapperRef.current;
        if (!wrap) return;
        let cleanup = null;
        const t = setTimeout(() => {
            // `.wx-scale` is the full-width strip (moves with the
            // horizontal scroll — its bounding box can be off-screen).
            // We need the fixed VIEWPORT that clips it: SVAR labels
            // that `.wx-timescale-viewport`.  Fall back to searching
            // for the first ancestor of the scale strip with
            // overflow:hidden if the class name changes in future.
            const scale = wrap.querySelector(".wx-scale");
            const chart = wrap.querySelector(".wx-chart");
            if (!scale || !chart) return;
            let viewport = wrap.querySelector(".wx-timescale-viewport");
            if (!viewport) {
                for (let el = scale.parentElement;
                    el && el !== wrap; el = el.parentElement) {
                    const cs = getComputedStyle(el);
                    if (cs.overflowX === "hidden" || cs.overflowX === "clip") {
                        viewport = el;
                        break;
                    }
                }
            }
            if (!viewport) return;
            viewport.style.cursor = "grab";
            let dragging = false, x0 = 0, s0 = 0;
            const down = (e) => {
                if (e.button !== 0) return;
                if (!e.target.closest || !e.target.closest(".wx-scale")) return;
                dragging = true;
                x0 = e.clientX; s0 = chart.scrollLeft;
                viewport.style.cursor = "grabbing";
                e.preventDefault();
            };
            const move = (e) => {
                if (!dragging) return;
                const newLeft = Math.max(0, s0 - (e.clientX - x0));
                if (svarApiRef.current) {
                    try {
                        svarApiRef.current.exec("scroll-chart",
                            { left: newLeft });
                        return;
                    } catch (_) { /* fall through */ }
                }
                chart.scrollLeft = newLeft;
            };
            const up = () => {
                if (dragging) {
                    dragging = false;
                    viewport.style.cursor = "grab";
                }
            };
            viewport.addEventListener("pointerdown", down);
            window.addEventListener("pointermove", move);
            window.addEventListener("pointerup", up);
            cleanup = () => {
                viewport.removeEventListener("pointerdown", down);
                window.removeEventListener("pointermove", move);
                window.removeEventListener("pointerup", up);
                if (viewport) viewport.style.cursor = "";
            };
        }, 400);
        return () => {
            clearTimeout(t);
            if (cleanup) cleanup();
        };
    }, [loading, projects.length]);

    /* -------- Colour override <style> block -------------------------- */
    const colourStyle = useMemo(() => {
        // SVAR prepends `:` to task DOM `data-id` attributes and
        // applies bar colour differently for tasks vs milestones:
        //   .wx-bar.wx-task     — background on the bar element itself
        //   .wx-bar.wx-milestone — visible diamond is a `.wx-content`
        //     child rotated 45° with its own background
        // We use `[data-id$="ID"]` so we don't have to care about the
        // colon prefix.
        const rules = items.map(it => {
            const c = colours[it.category] || DEFAULT_COLOURS[it.category]
                || DEFAULT_COLOURS.other;
            const idSel = `[data-id$="${it.id}"]`;
            return `.planner-shell .wx-bar.wx-task${idSel} {
                        background-color: ${c} !important;
                        border-color: ${c} !important;
                    }
                    .planner-shell .wx-bar.wx-milestone${idSel} .wx-content {
                        background-color: ${c} !important;
                        border-color: ${c} !important;
                    }`;
        });
        return rules.join("\n");
    }, [items, colours]);

    /* -------- Settings cogwheel handlers ----------------------------- */
    const onColourChange = useCallback((cat, hex) => {
        // Update local state immediately; debounce the server write.
        const next = { ...colours, [cat]: hex };
        setPreferences(prev => ({
            ...(prev || {}),
            category_colours: next,
            project_order: prev?.project_order || [],
        }));
        queuePrefsSave({ category_colours: next });
    }, [colours, queuePrefsSave]);
    const onColourReset = useCallback(() => {
        setPreferences(prev => ({
            ...(prev || {}),
            category_colours: { ...DEFAULT_COLOURS },
            project_order: prev?.project_order || [],
        }));
        queuePrefsSave({ category_colours: { ...DEFAULT_COLOURS } });
    }, [queuePrefsSave]);

    /* -------- Header ------------------------------------------------- */
    return (
        <div className="flex flex-col gap-6 planner-shell"
             ref={wrapperRef} data-testid="planner-page">
            <div className="flex items-start justify-between">
                <div>
                    <h1 className="font-manrope text-3xl font-semibold text-charcoal mb-1"
                        data-testid="planner-page-title">
                        Planner
                    </h1>
                    <p className="text-[#666] text-[15px]" data-testid="planner-page-subtitle">Cross-project programme overview.</p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={() => setYear(y => y - 1)}
                            className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md text-charcoal hover:bg-[#EBE8E0]"
                            data-testid="planner-prev-year">
                        ← {year - 1}
                    </button>
                    <div className="text-[14px] font-manrope font-semibold text-charcoal px-2"
                         data-testid="planner-current-year">{year}</div>
                    <button onClick={() => setYear(y => y + 1)}
                            className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md text-charcoal hover:bg-[#EBE8E0]"
                            data-testid="planner-next-year">
                        {year + 1} →
                    </button>
                    <button onClick={() => {
                                setYear(new Date().getFullYear());
                                setTimeout(scrollToCurrentMonth, 350);
                            }}
                            className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md text-charcoal hover:bg-[#EBE8E0]"
                            data-testid="planner-today">This year</button>
                    <button onClick={() => setShowSettings(true)}
                            className="text-[13px] p-1.5 border border-[#E5E2DC] rounded-md text-charcoal hover:bg-[#EBE8E0]"
                            title="Category colours"
                            aria-label="Category colours"
                            data-testid="planner-settings-open">
                        <Settings size={15} />
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="text-[13px] text-[#666]" data-testid="planner-loading">
                    Loading…
                </div>
            ) : projects.length === 0 ? (
                <div className="text-[13px] text-[#666] py-6" data-testid="planner-empty">
                    No active projects on WS 2 or beyond.
                    Move a project onto WS 2 to see it here.
                </div>
            ) : (
                <div className="border border-[#E5E2DC] rounded-md bg-white overflow-hidden"
                     style={{
                         // Reserve less viewport chrome so more rows fit.
                         // AdminLayout <main> has p-6/md:p-10 (24-40px);
                         // page H1+gap is ~64px.  Total chrome ~104px
                         // on md screens; keeping 140 leaves a safe
                         // margin without clipping.
                         height: "calc(100vh - 140px)",
                         minHeight: 500,
                     }}
                     data-testid="planner-gantt-wrap">
                    <Willow>
                        <Gantt
                            tasks={svarData.tasks}
                            scales={scales}
                            columns={columns}
                            start={rangeStart}
                            end={rangeEnd}
                            cellWidth={100}
                            cellHeight={44}
                            scaleHeight={60}
                            init={initGantt}
                        />
                    </Willow>
                </div>
            )}

            {dialog && (
                <PlannerItemDialog
                    mode={dialog.mode}
                    initialItem={dialog.initialItem}
                    projects={projects}
                    lockProject={!!dialog.lockProject}
                    onClose={() => setDialog(null)}
                    onSaved={onSaved}
                    onDeleted={onDeleted}
                />
            )}
            {showSettings && (
                <ColourSettingsPanel
                    colours={colours}
                    onChange={onColourChange}
                    onReset={onColourReset}
                    onClose={() => setShowSettings(false)}
                />
            )}
            {/* ============================================================
               Scoped SVAR style overrides — all rules live inside
               .planner-shell so they never leak to other admin pages.
               Design tokens reused verbatim from ProjectsLanding /
               B3bPages so the Planner reads as part of the same admin.
               ============================================================ */}
            <style>{`
                /* ---- typography ---------------------------------- */
                .planner-shell,
                .planner-shell .wx-scale,
                .planner-shell .wx-body,
                .planner-shell .wx-header,
                .planner-shell .wx-cell,
                .planner-shell .wx-row,
                .planner-shell .wx-bar,
                .planner-shell .wx-bar .wx-text,
                .planner-shell .wx-bar .wx-content {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont,
                                 'Segoe UI', Roboto, sans-serif !important;
                    color: #1D1D1B;
                }
                /* Two-line week cell: "W1" on top, dd/mm-dd/mm
                   underneath.  Both lines are regular weight. */
                .planner-shell .planner-week-cell {
                    white-space: pre-line !important;
                    line-height: 1.25;
                    text-align: center;
                    font-variant-numeric: tabular-nums;
                    font-size: 9px;
                    font-weight: 400;
                    color: #666;
                    padding: 4px 6px !important;
                }
                .planner-shell .planner-week-cell::first-line {
                    font-size: 10px;
                    font-weight: 400;
                    color: #1D1D1B;
                }
                /* ---- shell + borders ----------------------------- */
                /* Willow wraps <Gantt> in TWO .wx-theme divs.  Without
                   an explicit height, .wx-theme collapses to its
                   content-based intrinsic size, which causes SVAR to
                   size .wx-gantt to the full row-list height and
                   overflow the wrapper (documented on SVAR forums).
                   Forcing .wx-theme to 100% of its parent closes the
                   chain so SVAR's own overflow-y:auto can scroll. */
                .planner-shell .wx-theme {
                    height: 100% !important;
                }
                .planner-shell .wx-gantt {
                    border: 0 !important;
                    background: #FFFFFF;
                    height: 100% !important;
                    max-height: 100% !important;
                }
                /* Grid = the left column area; chart = right-hand
                   timeline.  We want a hairline between them
                   matching the Projects list card borders. */
                .planner-shell .wx-grid {
                    border-right: 1px solid #E5E2DC !important;
                    background: #FFFFFF;
                }
                /* ---- header rhythm ------------------------------- */
                .planner-shell .wx-scale .wx-row {
                    border-bottom: 1px solid #E5E2DC !important;
                }
                .planner-shell .wx-scale .wx-cell {
                    border-right: 1px solid #EFECE5 !important;
                    padding: 4px 8px !important;
                }
                /* "Projects" column header. */
                .planner-shell .wx-header {
                    background: #FAFAF7 !important;
                    color: #1D1D1B !important;
                    font-family: 'Manrope', 'Inter', sans-serif !important;
                    font-weight: 600 !important;
                    font-size: 13px !important;
                    letter-spacing: 0.01em;
                    padding: 0 16px !important;
                    border-bottom: 1px solid #E5E2DC !important;
                }
                /* ---- rows ---------------------------------------- */
                .planner-shell .wx-body .wx-row {
                    border-bottom: 1px solid #E5E2DC !important;
                    background: #FFFFFF !important;
                }
                .planner-shell .wx-body .wx-row:hover {
                    background: #FAFAF7 !important;
                }
                .planner-shell .wx-body .wx-cell {
                    padding: 0 16px !important;
                    border-right: none !important;
                }
                /* Chart-side row lines: hairline every week. */
                .planner-shell .wx-chart .wx-row {
                    border-bottom: 1px solid #E5E2DC !important;
                }
                .planner-shell .wx-chart .wx-row .wx-cell,
                .planner-shell .wx-chart .wx-cell {
                    border-right: 1px solid #F1EEE7 !important;
                }
                /* ---- bars & milestones ---------------------------
                   Vertical-centring maths (cellHeight = 44):
                     SVAR positions every .wx-bar absolutely with
                     inline 'top = Rs + cellHeight * rowIdx' (Rs = 3)
                     and inline 'height = cellHeight - St' (St = 7,
                     so 37px).  We do NOT touch SVAR's inline 'top'
                     — overriding it with 'top: 50%' breaks every
                     bar because 50% resolves against '.wx-bars'
                     (the whole chart), not the individual row.
                     Instead we override height and shift with
                     'transform: translateY(...)', which composes
                     on top of the inline 'top'.

                     For a 20px task bar to be centred in a 44px
                     row we need top = 12 + 44 * rowIdx, i.e.
                     +9px relative to SVAR's inline top:
                       (cellHeight - barHeight) / 2 - Rs
                       = (44 - 20) / 2 - 3 = 9. */
                .planner-shell .wx-bar.wx-task {
                    border-radius: 4px !important;
                    border: none !important;
                    box-shadow: 0 1px 0 rgba(0,0,0,0.06);
                    height: 20px !important;
                    transform: translateY(9px) !important;
                }
                .planner-shell .wx-bar.wx-task .wx-text,
                .planner-shell .wx-bar.wx-task .wx-content {
                    color: #FFFFFF !important;
                    font-size: 10px !important;
                    font-weight: 500 !important;
                    padding: 0 6px !important;
                    line-height: 20px !important;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    display: flex !important;
                    align-items: center;
                }
                /* Milestone: SVAR sizes the container to $h × $h
                   (37×37) at inline top = 3 + 44 * rowIdx, which
                   is already roughly vertically-centred within the
                   44px row (container centre y = row-top + 21.5,
                   row centre y = row-top + 22 — 0.5px off).  We
                   just centre the smaller 14×14 diamond inside
                   that 37×37 container.  Do NOT stretch the
                   container to height 100% — that used to pull it
                   down to full chart height and pin the diamond to
                   the row top. */
                .planner-shell .wx-bar.wx-milestone .wx-content {
                    width: 14px !important;
                    height: 14px !important;
                    border-radius: 2px !important;
                    box-shadow: 0 1px 0 rgba(0,0,0,0.08);
                    top: 50% !important;
                    left: 50% !important;
                    transform: translate(-50%, -50%) rotate(45deg) !important;
                }
                /* Hide all label text inside the chart area — both
                   the label next to a milestone diamond and the label
                   inside a programme bar.  SVAR uses two different
                   class names: .wx-content holds the text INSIDE a
                   .wx-bar.wx-task (but is also the diamond visual on
                   a milestone — so we must NOT target it globally),
                   and .wx-text-out holds the milestone label.
                   Kept in the DOM (display:none rather than removing)
                   so screen readers still expose the item title. */
                .planner-shell .wx-bar.wx-task .wx-content,
                .planner-shell .wx-bar.wx-milestone .wx-text-out {
                    display: none !important;
                    font-size: 0 !important;
                    color: transparent !important;
                }
                /* Hide the placeholder bar SVAR draws for project
                   parents. Project data-ids contain ":p:" segment. */
                .planner-shell .wx-bar[data-id*=":p:"],
                .planner-shell .wx-bar[data-id^="p:"] {
                    display: none !important;
                }
                /* Hide progress marker + dependency dots — planner
                   items don't carry progress or link data. */
                .planner-shell .wx-progress-marker,
                .planner-shell .wx-progress-wrapper,
                .planner-shell .wx-link.wx-target {
                    display: none !important;
                }
                /* ---- category colours (must be after generic bar
                       rules so they win specificity/order). ---- */
                ${colourStyle}
            `}</style>
        </div>
    );
}
